{
    This file is part of the Fresnel Library.
    Copyright (c) 2025 by the FPC & Lazarus teams.

    Pas2js Fresnel interface - menu builder interface.

    See the file COPYING.modifiedLGPL.txt, included in this distribution,
    for details about the copyright.

    This program is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

 **********************************************************************}
{$mode objfpc}
{$h+}
{$modeswitch externalclass}
{$modeswitch advancedrecords}

{$DEFINE IMAGE_USEOSC}

unit Fresnel.MenuBuilder.Pas2js.WasmAPI;

// Define this to disable API Logging altogether
{$DEFINE NOLOGAPICALLS}

interface

uses
  SysUtils,
  Web,
  Fresnel.Wasm.Shared,
  Fresnel.Shared.Pas2js;

 type

  { TMainMenuBuilder }

  TMenuClickCallback = procedure (aMenuID : TMenuID; aUserData : TWasmPointer);

  TMenuFlag = (mfInvisible,mfChecked,mfRadio);
  TMenuFlags = set of TMenuFlag;

  TMainMenuBuilder = class(TObject)
  private
    FApi: TWasmFresnelSharedApi;
    FMenuParent: TJSHTMLElement;
  protected
    Procedure DoMenuClick(aEvent : TJSEvent);
    function DoAddMenuItem(aParentID,aMenuID : TMenuID; aCaption : String; Flags : TMenuFlags; ShortCut : LongInt; aData : TWasmPointer) : TJSHTMLElement; virtual; abstract;
    function DoRemoveMenuItem(aMenuID : TMenuID) : Boolean; virtual; abstract;
    function DoUpdateMenuItem(aMenuID : TMenuID; aFlags : TMenuFlags; aFlagsToUpdate : TMenuFlags) : Boolean; virtual; abstract;
  public
    Constructor Create(aApi : TWasmFresnelSharedApi; aMenuParent : TJSHTMLElement); virtual;
    function AddMenuItem(aParentID,aMenuID : TMenuID; aCaption : String; Flags : TMenuFlags; ShortCut : LongInt; aData : TWasmPointer) : TJSHTMLElement;
    function RemoveMenuItem(aMenuID : TMenuID) : Boolean;
    function UpdateMenuItem(aMenuID : TMenuID; aFlags : TMenuFlags; aFlagsToUpdate : TMenuFlags) : Boolean;

    property MenuParent : TJSHTMLElement read FMenuParent;
    property API : TWasmFresnelSharedApi read FApi;
  end;

  { TDefaultMainMenuBuilder }

  TDefaultMainMenuBuilder = class(TMainMenuBuilder)
  protected
    function FindMenuElement(aMenuID: Integer): TJSHTMLElement;
    function DoAddMenuItem(aParentID, aMenuID: TMenuID; aCaption: String; Flags : TMenuFlags; ShortCut : LongInt; aData: TWasmPointer): TJSHTMLElement; override;
    function DoRemoveMenuItem(aMenuID : TMenuID) : Boolean; override;
    function DoUpdateMenuItem(aMenuID : TMenuID; aFlags : TMenuFlags; aFlagsToUpdate : TMenuFlags) : Boolean; override;
  end;

// --------------------------------------------------------------------
// --------------------------------------------------------------------
// --------------------------------------------------------------------
implementation
// --------------------------------------------------------------------
// --------------------------------------------------------------------
// --------------------------------------------------------------------

{ TMainMenuBuilder }

// Create
//
constructor TMainMenuBuilder.Create(aApi: TWasmFresnelSharedApi; aMenuParent: TJSHTMLElement);
begin
  FAPI:=aApi;
  FMenuParent:=aMenuParent;
end;

function TMainMenuBuilder.AddMenuItem(aParentID,aMenuID: TMenuID; aCaption: String; Flags : TMenuFlags; ShortCut : LongInt; aData: TWasmPointer): TJSHTMLElement;
begin
  Result:=DoAddMenuItem(aParentID,aMenuID,aCaption,Flags,ShortCut,aData);
end;

function TMainMenuBuilder.RemoveMenuItem(aMenuID: TMenuID): Boolean;
begin
  Result:=DoRemoveMenuItem(aMenuID);
end;

function TMainMenuBuilder.UpdateMenuItem(aMenuID: TMenuID; aFlags: TMenuFlags; aFlagsToUpdate: TMenuFlags): Boolean;
begin
  Result:=DoUpdateMenuItem(aMenuID,aFlags,aFlagsToUpdate);
end;

procedure TMainMenuBuilder.DoMenuClick(aEvent: TJSEvent);
var
  S : String;
  MenuID : integer;
  UserData : TWasmPointer;
  menuEl : TJSHTMLElement;
begin
  MenuEl:=TJSHTMLElement(aEvent.currentTargetHTMLElement.parentElement);
  S:=MenuEl.dataset['menuId'];
  MenuID:=StrToIntDef(S,-1);
  S:=MenuEl.dataset['menuUserData'];
  UserData:=StrToIntDef(S,-1);
  if (UserData<>-1) and (MenuID<>-1) then
    API.HandleMenuClick(MenuID,UserData);
end;

{ TDefaultMainMenuBuilder }

function TDefaultMainMenuBuilder.FindMenuElement(aMenuID : Integer) : TJSHTMLElement;

begin
  Result:=TJSHTMLElement(MenuParent.querySelector('li[data-menu-id="'+IntToStr(aMenuID)+'"]'))
end;

function TDefaultMainMenuBuilder.DoAddMenuItem(aParentID,aMenuID: TMenuID; aCaption: String; Flags : TMenuFlags; ShortCut : LongInt; aData: TWasmPointer): TJSHTMLElement;

var
  CaptionEl,MenuEl,ListEl,parentEl : TJSHTMLElement;

begin
  Result:=nil;
  if AParentID=0 then
    ParentEl:=MenuParent
  else
    ParentEl:=FindMenuElement(aParentID);
  if Not assigned(ParentEl) then exit;
  ListEl:=TJSHTMLElement(ParentEl.QuerySelector('ul'));
  if Not Assigned(ListEl) then
    begin
    ListEl:=TJSHTMLElement(Document.createElement('ul'));
    if ParentEl=MenuParent then
      ListEl.classList.Add('fresnel-mainmenu')
    else
      ListEl.classList.Add('fresnel-submenu');
    ParentEl.appendChild(ListEl);
    end;
  MenuEl:=TJSHTMLElement(Document.CreateElement('li'));
  ListEl.appendChild(MenuEl);
  MenuEl.dataset.Map['menuId']:=IntToStr(aMenuID);
  MenuEl.dataset.Map['menuUserData']:=IntToStr(aData);
  if aCaption='-' then
    begin
    MenuEl.ClassList.add('fresnel-menu-separator');
    CaptionEl:=TJSHTMLElement(Document.CreateElement('hr'))
    end
  else
    begin
    MenuEl.classList.Add('fresnel-menu-item');
    CaptionEl:=TJSHTMLElement(Document.CreateElement('span'));
    if mfChecked in Flags then
      aCaption:=#$2611+' '+aCaption;
    CaptionEl.InnerText:=aCaption;
    CaptionEl.AddEventListener('click',@DoMenuClick);
    end;
  MenuEl.appendChild(CaptionEl);
  Result:=MenuEl;
end;

function TDefaultMainMenuBuilder.DoRemoveMenuItem(aMenuID: TMenuID) : Boolean;

var
  El : TJSHTMLElement;

begin
  El:=FindMenuElement(aMenuID);
  Result:=Assigned(El);
  if Result then
    El.parentElement.removeChild(El);
end;

function TDefaultMainMenuBuilder.DoUpdateMenuItem(aMenuID: TMenuID; aFlags: TMenuFlags; aFlagsToUpdate: TMenuFlags): Boolean;
begin
  Result:=False;
end;



end.

